import React, { FunctionComponent, useEffect, useMemo, useRef } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { usePagination, useRowSelect, useTable } from 'react-table';

import { Table } from 'core/table/basic/Table';
import { TablePagination } from 'core/table/pagination/TablePagination';
import { Variant } from 'core/util/Bootstrap';
import { getDefaultColumns } from 'directory/listings/notification-modals/components/DataTable/tableColumns';
import { IndeterminateCheckbox } from 'directory/listings/notification-modals/components/IndeterminateCheckbox';
import { PaginationInfo } from 'directory/listings/notification-modals/components/PaginationInfo';
import { DEFAULT_PAGE_SIZE, TRANSLATION_NAMESPACE } from 'directory/listings/notification-modals/constants';
import { DataTableProps } from 'directory/listings/notification-modals/types';

import 'directory/listings/notification-modals/components/DataTable/DataTable.scss';

export const DataTable: FunctionComponent<DataTableProps> = ({ data, config = {}, onRowSelect }) => {
    const { t } = useTranslation(TRANSLATION_NAMESPACE);
    const {
        showCheckboxes = true,
        pageSize = DEFAULT_PAGE_SIZE,
        columns: customColumns,
        enableZebraStriping = true,
        enableHover = false,
        isCompletionView = false,
    } = config;

    const tableHeaderRef = useRef<HTMLTableSectionElement>(null);
    const tbodyRef = useRef<HTMLTableSectionElement>(null);

    const columns = useMemo(() => {
        if (customColumns) return customColumns;
        return getDefaultColumns(data, isCompletionView);
    }, [customColumns, data, isCompletionView]);

    const initialSelectedRowIds = useMemo(() => {
        if (!showCheckboxes) {
            return {};
        }
        return data.reduce((acc, row, index) => {
            acc[index] = true;
            return acc;
        }, {} as Record<string, boolean>);
    }, [data, showCheckboxes]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canNextPage,
        canPreviousPage,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        selectedFlatRows,
        state: { pageIndex, selectedRowIds },
    } = useTable(
        {
            columns,
            data,
            initialState: {
                pageSize,
                selectedRowIds: initialSelectedRowIds,
            },
        },
        usePagination,
        useRowSelect,
        (hooks) => {
            if (showCheckboxes) {
                hooks.visibleColumns.push((columns) => [
                    {
                        id: 'selection',
                        Header: ({ getToggleAllRowsSelectedProps }: any) => (
                            <div className="data-table__checkbox-cell">
                                <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                            </div>
                        ),
                        Cell: ({ row }: any) => (
                            <div className="data-table__checkbox-cell">
                                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                            </div>
                        ),
                    },
                    ...columns,
                ]);
            }
        },
    );

    useEffect(() => {
        const updateHeight = () => {
            if (tbodyRef.current && tableHeaderRef.current) {
                const tbodyHeight = tbodyRef.current.scrollHeight;

                tableHeaderRef.current.style.setProperty('--tbody-height', `${tbodyHeight}px`);
            }
        };

        updateHeight();

        const resizeObserver = new ResizeObserver(updateHeight);

        if (tbodyRef.current) {
            resizeObserver.observe(tbodyRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [page, data]);

    useEffect(() => {
        if (onRowSelect && showCheckboxes) {
            const selected = selectedFlatRows.map((row) => row.original);

            onRowSelect(selected);
        }
    }, [selectedRowIds, selectedFlatRows, onRowSelect, showCheckboxes]);

    if (data.length === 0) {
        return (
            <div className="data-table data-table--empty">
                <p>{t('NO_DATA_AVAILABLE')}</p>
            </div>
        );
    }

    return (
        <div
            className={classNames('data-table', {
                'data-table--hover-enabled': enableHover,
                'data-table--no-checkboxes': !showCheckboxes,
            })}
        >
            <div className="data-table__container">
                <Table
                    bordered
                    hover={enableHover}
                    variant={Variant.Default}
                    className="data-table__table"
                    {...getTableProps()}
                >
                    <thead className="data-table__header" ref={tableHeaderRef}>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column, columnIndex) => (
                                    <th
                                        {...column.getHeaderProps()}
                                        className={classNames({
                                            'data-table__header-checkbox': showCheckboxes && columnIndex === 0,
                                            'data-table__header-google': showCheckboxes
                                                ? columnIndex === 2
                                                : columnIndex === 1,
                                            'data-table__header-reputation': showCheckboxes
                                                ? columnIndex === 3
                                                : columnIndex === 2,
                                        })}
                                    >
                                        {column.render('Header')}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="data-table__body" ref={tbodyRef} {...getTableBodyProps()}>
                        {page.map((row, rowIndex) => {
                            prepareRow(row);
                            return (
                                <tr
                                    {...row.getRowProps()}
                                    className={classNames('data-table__row', {
                                        'data-table__row--even': enableZebraStriping && rowIndex % 2 === 0,
                                        'data-table__row--odd': enableZebraStriping && rowIndex % 2 !== 0,
                                    })}
                                >
                                    {row.cells.map((cell, cellIndex) => (
                                        <td
                                            {...cell.getCellProps()}
                                            className={classNames('data-table__cell', {
                                                'data-table__cell-checkbox': showCheckboxes && cellIndex === 0,
                                                'data-table__cell-google': showCheckboxes
                                                    ? cellIndex === 2
                                                    : cellIndex === 1,
                                                'data-table__cell-reputation': showCheckboxes
                                                    ? cellIndex === 3
                                                    : cellIndex === 2,
                                            })}
                                        >
                                            {cell.render('Cell')}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>

            <div className="data-table__footer">
                <PaginationInfo currentPage={pageIndex} pageSize={pageSize} totalItems={data.length} />
                <TablePagination
                    canNextPage={canNextPage}
                    canPreviousPage={canPreviousPage}
                    gotoPage={gotoPage}
                    nextPage={nextPage}
                    pageIndex={pageIndex}
                    pageCount={pageCount}
                    previousPage={previousPage}
                    showXofY={false}
                />
            </div>
        </div>
    );
};
